import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { ModalFooter, Button } from "@racoon/ui";
import { useT, Namespace, useApiErrorMessage } from "@racoon/i18n";
import { useListTruckDriverOnShiftsQuery } from "~/shared/api/shift/driver-on-shift.grpc";
import { getUtcDayRange } from "~/pages/dashboard/routes/utils/dayRange";
import { useListJobsQuery } from "~/shared/api/jobs/job.grpc";
import {
  useGetLocationByIdQuery,
  useListDestinationLocationsQuery,
} from "~/shared/api/locations/locations.grpc";
import { useGetOrganizationSettingsQuery } from "~/shared/api/organization/organization.grpc";
import { useListBlacklistsQuery } from "~/shared/api/blacklist/blacklist.grpc";
import { useGetTruckTypeByIdQuery } from "~/shared/api/truck/truck-type.api";
import {
  useLazyGetDepartInfoQuery,
  useCalculateRouteWaypointsMutation,
} from "~/shared/api/cost-provider/cost-provider.api";
import {
  useCreateRoutesFromStopsMutation,
  useCreateRouteWithJobsMutation,
} from "~/shared/api/routes/route.grpc";
import { useCancelJobMutation, useCreateJobMutation } from "~/shared/api/jobs/job.grpc";
import { useSolveSingleVehicleMutation } from "~/shared/api/routes/vrp-routing.grpc";
import { ConnectProto, type ApiError } from "@racoon/api";
import { isShiftRunning, isShiftTerminal } from "~/shared/lib/shift-status";
import { IcCancel, IcError } from "@racoon/icons";
import { RouteSidebar } from "./components/route-sidebar";
import { ShiftHeaderCard } from "./components/shift-header-card";
import { SortableJobList } from "./components/sortable-job-list";
import { JobIssue } from "./components/sortable-job-row";
import { DepotBar } from "./components/depot-bar";
import { DepotChangeConfirmModal } from "./components/depot-change-confirm-modal";
import { TripTabs } from "./components/trip-tabs";
import { useJobPickerModal } from "~/shared/ui/job-picker-modal";
import {
  isJobServableByTruck,
  isSingleJobPerTripTruckType,
} from "~/pages/dashboard/routes/hooks/msw-route-list/jobCategoryRouteCompatibility";
import { DraftJob, isDraftJobId } from "../model/draft-jobs";
import { routeOwnerOf } from "~/shared/lib/route-owner";
import type { GroupStops } from "../hooks/use-assign-route-modal";
import { useConfirmDuplicates } from "../hooks/use-confirm-duplicates";
import { MapWithGeofence, MapObject } from "~/features/map-view/ui/map-view";
import { buildStepPins } from "~/features/map-view/model/helpers/step-pin-items";
import { InfoType } from "~/features/info-panel/model/useInfoPanel";
import { useReissueExpiredJobs } from "~/shared/hooks/use-reissue-expired-jobs";
import {
  calculateTotalRouteTimeWithService,
  filterTrucks,
  isTruckFree,
} from "../utils/route-calculations";
import { MapVisibilityStore, useMapVisible } from "../model/map-visibility";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useJobExecutorIds } from "~/shared/hooks/use-is-own-organization";
import { toTimestamp, normalizeForSearch } from "@racoon/lib";
import { parseISO8601Duration } from "~/shared/lib/formatters";
import { plannedContainerCount } from "~/shared/lib/job-containers";

const JOB_CATEGORY_TO_GRPC_ROUTE_CATEGORY: Partial<
  Record<ConnectProto.JobCategory, ConnectProto.RouteCategory>
> = {
  [ConnectProto.JobCategory.CO]: ConnectProto.RouteCategory.CO,
  [ConnectProto.JobCategory.MSW]: ConnectProto.RouteCategory.MSW,
  [ConnectProto.JobCategory.DISINFECTION]: ConnectProto.RouteCategory.DISINFECTION,
  [ConnectProto.JobCategory.LIQUID]: ConnectProto.RouteCategory.LIQUID,
  [ConnectProto.JobCategory.TIPPER]: ConnectProto.RouteCategory.TIPPER,
};

const FOOTER_MESSAGE_TONES = {
  error: "text-brand-red",
  warning: "text-amber-600",
  success: "text-green-600",
} as const;

// Compact, dismissible footer notice. Lives next to the overload indicator so
// every problem is shown right where the blocked action is.
const FooterMessage: React.FC<{
  tone: keyof typeof FOOTER_MESSAGE_TONES;
  text: string;
  // Extra context (raw errors) — kept in the tooltip to keep the footer one line.
  details?: string;
  closeLabel: string;
  onDismiss: () => void;
}> = ({ tone, text, details, closeLabel, onDismiss }) => (
  <div
    className={`flex min-w-0 items-center gap-1 text-sm font-medium ${FOOTER_MESSAGE_TONES[tone]}`}
    title={details || text}
  >
    {tone !== "success" && <IcError className="h-6 w-6 shrink-0" />}
    <span className="max-w-[420px] truncate">{text}</span>
    <button
      type="button"
      onClick={onDismiss}
      className="shrink-0 opacity-70 transition-opacity hover:opacity-100"
      aria-label={closeLabel}
    >
      <IcCancel className="h-5 w-5" />
    </button>
  </div>
);

interface AssignRouteContentProps {
  preselectedTruckId?: string;
  // Jobs the first trip opens with (see OpenAssignRouteOptions).
  preselectedJobs?: ConnectProto.Job[];
  // Jobs that do not exist yet, created on submit (see OpenAssignRouteOptions).
  draftJobs?: DraftJob[];
  // Group the route is built out of, and its stops on another day (see
  // OpenAssignRouteOptions).
  pickupLocationGroupId?: bigint;
  loadGroupStops?: (date: Date) => Promise<GroupStops>;
  // The group's template mode: bulk create reuses free jobs and duplicates the
  // rest without asking.
  groupTemplate?: boolean;
  // The day the modal opens on, today when omitted (see OpenAssignRouteOptions).
  initialDate?: Date;
  // Called once the routes are created (see OpenAssignRouteOptions).
  onCreated?: () => void;
  // Toggled from the modal header; when off the map panel is unmounted.
  mapVisibility: MapVisibilityStore;
}

// A job's time window: the whole shift day it is planned for, which is what the
// order form's default ("today until 23:59") amounts to for same-day work.
const startOfDay = (date: Date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
};

const endOfDay = (date: Date) => {
  const end = new Date(date);
  end.setHours(23, 59, 59, 0);
  return end;
};

// Trip depot meaning "finish without unloading": the backend completes a route
// with no destination right after its last job.
const NO_UNLOADING = -1;

// A single trip (рейс) within the route being created. Each trip has its own
// ordered list of job slots and its own unloading depot.
interface Trip {
  id: string;
  jobSlots: (number | null)[];
  depot: number | null;
}

const makeEmptyTrip = (id: string, depot: number | null = null): Trip => ({
  id,
  jobSlots: [null],
  depot,
});

export const AssignRouteContent: React.FC<AssignRouteContentProps> = ({
  preselectedTruckId,
  preselectedJobs,
  draftJobs,
  pickupLocationGroupId,
  loadGroupStops,
  groupTemplate = false,
  initialDate,
  onCreated,
  mapVisibility,
}) => {
  const t = useT();
  const describeError = useApiErrorMessage();
  const { openJobPicker } = useJobPickerModal();
  const isMapVisible = useMapVisible(mapVisibility);

  // Jobs the modal opens with: rows checked in a table (real, positive ids) and
  // drafts a pickup location group expanded into (synthetic, negative ids).
  // Both travel the same paths from here on.
  const seedJobs = useMemo(
    () => [...(preselectedJobs ?? []), ...(draftJobs ?? []).map((draft) => draft.job)],
    [preselectedJobs, draftJobs],
  );

  // A group's drafts are replaced when the date changes (handleDateChange).
  const [drafts, setDrafts] = useState(() => draftJobs ?? []);

  // A group's duplicates: drafts of windows whose job that day is routed, in
  // progress or another group's, by the job they duplicate. The backend creates
  // them once the dispatcher confirms.
  const duplicateOfById = useMemo(
    () =>
      new Map(
        drafts.flatMap((draft) => (draft.duplicateOf ? [[draft.tempId, draft.duplicateOf]] : [])),
      ),
    [drafts],
  );
  const getJobDuplicateNote = useCallback(
    (jobId: number) => {
      const duplicateOf = duplicateOfById.get(jobId);
      return duplicateOf
        ? t(Namespace.Routes, "duplicateInWork", { id: String(duplicateOf) })
        : undefined;
    },
    [duplicateOfById, t],
  );

  // The create payload of every draft, by its synthetic id — the only place the
  // draft-specific data is needed, and only on submit.
  const draftCreateById = useMemo(
    () => new Map(drafts.map((draft) => [draft.tempId, draft.create])),
    [drafts],
  );

  // A trip slot as the backend's JobStop: a draft is its (container group, job
  // type) pair and the schedule window it was previewed for, a real job its id.
  const toJobStop = useCallback(
    (jobId: number) => {
      if (!isDraftJobId(jobId)) return { jobId: BigInt(jobId) };
      const draft = draftCreateById.get(jobId);
      return {
        containerGroupId: draft?.containerGroupId,
        jobType: draft?.type,
        twStartAt: draft?.twStartAt,
      };
    },
    [draftCreateById],
  );

  const [selectedTruck, setSelectedTruck] = useState("");
  const [trips, setTrips] = useState<Trip[]>(() =>
    seedJobs.length
      ? [{ id: "trip-1", jobSlots: seedJobs.map((job) => Number(job.id)), depot: null }]
      : [makeEmptyTrip("trip-1")],
  );
  const [activeTripId, setActiveTripId] = useState("trip-1");
  const tripIdCounter = useRef(1);
  const makeTripId = () => `trip-${++tripIdCounter.current}`;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobLimitError, setJobLimitError] = useState<string | null>(null);
  const [isTruckPopoverOpen, setIsTruckPopoverOpen] = useState(false);
  const [truckSearchQuery, setTruckSearchQuery] = useState("");
  const [lastRouteCalculation, setLastRouteCalculation] = useState<string>("");
  const [isDepotPopoverOpen, setIsDepotPopoverOpen] = useState(false);
  const [depotSearchQuery, setDepotSearchQuery] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isErrorDismissed, setIsErrorDismissed] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [optimizeError, setOptimizeError] = useState<string | null>(null);
  const [optimizeWarning, setOptimizeWarning] = useState<string | null>(null);
  // Trips currently come from the optimizer — the unloading point they were
  // solved for cannot be swapped without invalidating that distribution.
  const [isOptimized, setIsOptimized] = useState(false);
  // Whether a group's stops were optimized on their own yet (see below).
  const autoOptimizedRef = useRef(false);
  // Bumped by every solve and by whatever replaces the trips under one (date,
  // truck): only the latest solve's answer is applied.
  const optimizeRequestRef = useRef(0);
  const [pendingDepotId, setPendingDepotId] = useState<number | null>(null);

  // New Date and Shift states
  const [selectedDate, setSelectedDate] = useState<Date>(() => initialDate ?? new Date());
  const [selectedShiftId, setSelectedShiftId] = useState<string>("");

  // Active trip and back-compatible accessors so the rest of the logic can keep
  // working with `jobSlots` / `selectedDepot` scoped to the currently active trip.
  const activeTripIndex = Math.max(
    0,
    trips.findIndex((tr) => tr.id === activeTripId),
  );
  const activeTrip = trips[activeTripIndex] ?? trips[0];
  const jobSlots = activeTrip.jobSlots;
  const selectedDepot = activeTrip.depot;

  const setJobSlots: React.Dispatch<React.SetStateAction<(number | null)[]>> = (update) => {
    setTrips((prev) =>
      prev.map((tr) =>
        tr.id === activeTripId
          ? {
              ...tr,
              jobSlots:
                typeof update === "function"
                  ? (update as (prev: (number | null)[]) => (number | null)[])(tr.jobSlots)
                  : update,
            }
          : tr,
      ),
    );
  };

  const setSelectedDepot: React.Dispatch<React.SetStateAction<number | null>> = (update) => {
    setTrips((prev) =>
      prev.map((tr) =>
        tr.id === activeTripId
          ? {
              ...tr,
              depot:
                typeof update === "function"
                  ? (update as (prev: number | null) => number | null)(tr.depot)
                  : update,
            }
          : tr,
      ),
    );
  };

  const { activeCompanyId } = useAuthSession();
  // A route takes jobs its own companies execute, not those a responsible zone only shows.
  const executorIds = useJobExecutorIds();

  const dayRange = useMemo(() => {
    return getUtcDayRange(selectedDate);
  }, [selectedDate]);

  const { data: truckDriverShiftsResponse, error: trucksError } = useListTruckDriverOnShiftsQuery(
    {
      ownerOrganizationId: activeCompanyId ? BigInt(activeCompanyId) : 0n,
      datetimeFilter: {
        from: toTimestamp(new Date(dayRange.start)),
        to: toTimestamp(new Date(dayRange.end)),
      },
    },
    {
      skip: !activeCompanyId || !selectedDate,
    },
  );

  const truckDriverOnShifts = useMemo(() => {
    return truckDriverShiftsResponse?.truckDriverOnShifts ?? [];
  }, [truckDriverShiftsResponse]);

  const trucksWithShifts = useMemo(() => {
    return truckDriverOnShifts
      .map((item) => ({
        ...item,
        // A route can't be assigned to a shift that's over, so finished ones are
        // dropped from the tabs — but the full day's set is kept for numbering, or the
        // same shift ends up "Смена 1" here and "Смена 2" in the fleet.
        allShiftsOfDay: item.driverOnShifts ?? [],
        driverOnShifts: (item.driverOnShifts ?? [])
          .filter((shift) => !isShiftTerminal(shift.status))
          // Tabs in shift-number order, otherwise the API order reads "Смена 2, Смена 1".
          .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
      }))
      .filter((item) => !!item.truck);
  }, [truckDriverOnShifts]);
  const mappedCurrentTruckStatuses = useMemo<ConnectProto.CurrentTruckStatus[]>(() => {
    return trucksWithShifts.map((item) => {
      const activeShift = item.driverOnShifts?.find((shift) => isShiftRunning(shift.status));
      return {
        truck: item.truck,
        currentShift: activeShift ?? item.driverOnShifts?.[0],
      } as any;
    });
  }, [trucksWithShifts]);

  const selectedTruckData = useMemo(() => {
    return trucksWithShifts.find((item) => item.truck?.id.toString() === selectedTruck);
  }, [trucksWithShifts, selectedTruck]);

  const selectedShift = useMemo(() => {
    if (!selectedTruckData || !selectedTruckData.driverOnShifts) return undefined;
    return (
      selectedTruckData.driverOnShifts.find((s) => s.id.toString() === selectedShiftId) ||
      selectedTruckData.driverOnShifts[0]
    );
  }, [selectedTruckData, selectedShiftId]);

  // The route belongs to the shift's division, which need not be the active company.
  const routeOwnerId = routeOwnerOf(selectedShift, selectedTruckData?.truck, activeCompanyId);

  const currentTruckStatusForSelected = useMemo<ConnectProto.CurrentTruckStatus | null>(() => {
    if (!selectedTruckData?.truck) return null;
    return {
      truck: selectedTruckData.truck,
      currentShift: selectedShift,
    } as any;
  }, [selectedTruckData, selectedShift]);

  const {
    data: jobsResponse,
    error: jobsError,
    refetch: refetchJobs,
  } = useListJobsQuery(
    {
      serviceProviderIds: executorIds,
      executedOnly: true,
      jobStatuses: [ConnectProto.JobStatus.CREATED],
      // The backend keeps the jobs the truck type's capabilities cover; no category is
      // implied by the truck type (a самосвал also takes СО «Приём груза»).
      truckId: selectedTruck ? BigInt(selectedTruck) : undefined,
    },
    {
      // A default truck is picked as soon as the shift list loads, so querying
      // before that only fires a request whose result is instantly superseded
      // by the truck-scoped one. With no truck there is no route to build.
      skip: !activeCompanyId || !selectedTruck,
    },
  );

  const jobsData = useMemo(() => jobsResponse?.jobs ?? [], [jobsResponse]);

  // The picker has its own filters, so a picked job need not be in `jobsData` —
  // keeping it makes its address and meta resolvable regardless. Jobs the modal
  // was opened with are seeded here for the same reason: until a truck is chosen
  // there is no `jobsData` at all — and drafts never appear in it.
  const [pickedJobs, setPickedJobs] = useState<Record<string, ConnectProto.Job>>(() => {
    const seeded: Record<string, ConnectProto.Job> = {};
    seedJobs.forEach((job) => {
      seeded[job.id.toString()] = job;
    });
    return seeded;
  });

  const jobsById = useMemo(() => {
    const byId = new Map<number, ConnectProto.Job>();
    Object.values(pickedJobs).forEach((job) => byId.set(Number(job.id), job));
    // Server data wins over the snapshot taken when the job was picked.
    jobsData.forEach((job: ConnectProto.Job) => byId.set(Number(job.id), job));
    return byId;
  }, [jobsData, pickedJobs]);

  const findJob = useCallback(
    (jobId: number | null) => (jobId === null ? undefined : jobsById.get(Number(jobId))),
    [jobsById],
  );

  const jobsList = useMemo(() => [...jobsById.values()], [jobsById]);

  // --- Job ↔ truck compatibility ---------------------------------------------
  // Two rules the backend enforces on create, both keyed by the truck's *custom*
  // type: a blacklisted pickup location (orange) and the type's allowed
  // (job type, container type) capability pairs (red).
  const selectedTruckTypeId = selectedTruckData?.truck?.truckType?.id;

  // Every location in play at once, so adding a job does not refetch; sorted to
  // keep the cache key stable. BigInt(): ids are strings at runtime, since
  // connectBaseQuery stringifies every bigint it caches.
  const candidateLocationIds = useMemo(() => {
    const ids = jobsList
      .filter((job) => job.pickupLocationId)
      .map((job) => String(job.pickupLocationId));
    return [...new Set(ids)].sort().map(BigInt);
  }, [jobsList]);

  const { data: blacklistResponse } = useListBlacklistsQuery(
    {
      serviceProviderIds: activeCompanyId ? [BigInt(activeCompanyId)] : [],
      truckTypeIds: selectedTruckTypeId ? [BigInt(selectedTruckTypeId)] : [],
      pickupLocationIds: candidateLocationIds,
    },
    { skip: !activeCompanyId || !selectedTruckTypeId || candidateLocationIds.length === 0 },
  );

  const blacklistedLocationIds = useMemo(
    () =>
      new Set(
        (blacklistResponse?.blacklists ?? []).map((entry: ConnectProto.Blacklist) =>
          String(entry.pickupLocationId),
        ),
      ),
    [blacklistResponse],
  );

  // ListTruckDriverOnShifts embeds the custom truck type but not its capability
  // pairs, so they are fetched by type id whenever they did not come along.
  const embeddedCapabilities = useMemo<ConnectProto.TruckCapability[]>(
    () => selectedTruckData?.truck?.truckType?.truckTypeCapabilities ?? [],
    [selectedTruckData],
  );

  const { data: truckTypeDetails } = useGetTruckTypeByIdQuery(
    selectedTruckTypeId && activeCompanyId && embeddedCapabilities.length === 0
      ? { id: BigInt(selectedTruckTypeId), ownerOrganizationId: BigInt(activeCompanyId) }
      : skipToken,
  );

  const truckCapabilities = useMemo<ConnectProto.TruckCapability[]>(
    () =>
      embeddedCapabilities.length > 0
        ? embeddedCapabilities
        : (truckTypeDetails?.truckTypeCapabilities ?? []),
    [embeddedCapabilities, truckTypeDetails],
  );

  // Red outranks orange when a job breaks both rules. No capabilities loaded
  // means nothing to compare against — the backend still refuses on create.
  const getJobIssue = useCallback(
    (jobId: number): JobIssue | null => {
      const job = findJob(jobId);
      if (!job) return null;
      // A category with no route category (Не определен) can't go on any route: the backend
      // refuses it, so it is caught here before any draft gets created.
      if (JOB_CATEGORY_TO_GRPC_ROUTE_CATEGORY[job.jobCategory] == null) return "typeMismatch";

      const containerTypeId = job.containerGroup?.containerType?.id;
      const supported =
        truckCapabilities.length === 0 ||
        truckCapabilities.some(
          (capability) =>
            capability.jobType === job.type &&
            (!containerTypeId || String(capability.containerType?.id) === String(containerTypeId)),
        );
      if (!supported) return "typeMismatch";
      if (duplicateOfById.has(jobId)) return "duplicate";

      return blacklistedLocationIds.has(String(job.pickupLocationId)) ? "blacklisted" : null;
    },
    [findJob, truckCapabilities, blacklistedLocationIds, duplicateOfById],
  );

  const {
    data: locationResponse,
    isLoading: locationLoading,
    error: locationError,
  } = useListDestinationLocationsQuery(
    {
      ownerOrganizationIds: activeCompanyId ? [BigInt(activeCompanyId)] : [],
      types: [
        ConnectProto.LocationType.DISPOSAL_SITE,
        ConnectProto.LocationType.GAS_STATION,
        ConnectProto.LocationType.PROCESSING_PLANT,
        ConnectProto.LocationType.SORTING_COMPLEX,
        ConnectProto.LocationType.TRANSFER_STATION,
      ],
    },
    {
      skip: !activeCompanyId,
    },
  );

  const depotsData = useMemo(() => {
    if (!locationResponse?.locations) return [];
    // Archived locations are not unload targets for a new route.
    return locationResponse.locations
      .filter((d) => d.status === ConnectProto.LocationStatus.ACTIVE)
      .map((d) => ({
        ...d,
        id: Number(d.id),
        owner_organization_id: Number(d.ownerOrganizationId),
        service_time: d.serviceTime ? Number(d.serviceTime) : undefined,
      }));
  }, [locationResponse]);

  // Auto-select depot if there is only one
  useEffect(() => {
    if (depotsData.length === 1 && !selectedDepot) {
      setSelectedDepot(depotsData[0].id);
    }
  }, [depotsData, selectedDepot]);

  const [
    getDepartInfo,
    { currentData, isError: isDepartInfoError, isFetching: isDepartInfoFetching },
  ] = useLazyGetDepartInfoQuery();
  // `data` would keep the last success — another truck's shift, or this one's
  // point before its latest trip — and it is sent as the route's source. A failed
  // depart_info means no depart point, so the source falls back to the depot.
  const departInfo = isDepartInfoError ? undefined : currentData;

  // The org's default depot — where a trip without unloading departs from when
  // the shift has no depart point. GetSetting only knows the caller's own org.
  const { data: orgSettings } = useGetOrganizationSettingsQuery({});
  const defaultDepotId =
    orgSettings && Number(orgSettings.id) === activeCompanyId
      ? orgSettings.defaultDepotId
      : undefined;
  const { data: defaultDepot } = useGetLocationByIdQuery(
    { id: defaultDepotId ?? 0n, ownerOrganizationId: BigInt(activeCompanyId ?? 0) },
    { skip: !defaultDepotId || !activeCompanyId },
  );

  // Departure point of a trip: the shift's depart point, else the trip's
  // unloading depot, else the org's default depot, else the first pickup. The
  // last two only matter without unloading, where there is no depot to fall back
  // on. A pickup is not a Location, so it carries no id.
  const resolveSource = useCallback(
    (depot: { id: number; lat: number; lon: number } | undefined, firstJobId?: number) => {
      const fallback = depot ?? defaultDepot;
      const pickup = firstJobId != null ? findJob(firstJobId)?.pickupLocation : undefined;
      const lon = departInfo?.lon ?? fallback?.lon ?? pickup?.lon;
      const lat = departInfo?.lat ?? fallback?.lat ?? pickup?.lat;
      if (lon == null || lat == null) return null;
      const id = departInfo?.id ?? fallback?.id;
      return { lon: Number(lon), lat: Number(lat), id: id != null ? BigInt(id) : undefined };
    },
    [departInfo, defaultDepot, findJob],
  );

  const [
    calculateRouteWaypoints,
    { data: routeData, isLoading: isRouteCalculating, error: routeError },
  ] = useCalculateRouteWaypointsMutation();

  const [createRouteWithJobs, { isLoading: isCreateRouteLoading, error: createRouteError }] =
    useCreateRouteWithJobsMutation();

  const [createRoutesFromStops, { isLoading: isBulkCreateLoading }] =
    useCreateRoutesFromStopsMutation();

  const [createJob] = useCreateJobMutation();
  const [cancelJob] = useCancelJobMutation();
  const { reissueExpiredJobs } = useReissueExpiredJobs();
  const confirmDuplicates = useConfirmDuplicates();
  // Drafts are created before the routes, so the button has its own label for
  // that phase — it is the part that can take a while on a large group.
  const [isCreatingJobs, setIsCreatingJobs] = useState(false);

  const [solveSingleVehicle, { isLoading: isOptimizing }] = useSolveSingleVehicleMutation();

  // Set pre-selected truck when modal opens
  useEffect(() => {
    if (preselectedTruckId && trucksWithShifts.length > 0) {
      const truckExists = trucksWithShifts.find(
        (item) => String(item.truck?.id) === preselectedTruckId,
      );
      if (truckExists) {
        setSelectedTruck(preselectedTruckId);
      }
    }
  }, [preselectedTruckId, trucksWithShifts]);

  const filteredTrucks = useMemo(
    () => filterTrucks(mappedCurrentTruckStatuses, truckSearchQuery, isTruckFree),
    [mappedCurrentTruckStatuses, truckSearchQuery],
  );

  // Fetch depart info for the selected shift
  useEffect(() => {
    if (selectedShiftId) {
      getDepartInfo(Number(selectedShiftId));
    }
  }, [selectedShiftId, getDepartInfo]);

  // Set default selectedTruck when list changes or loads
  useEffect(() => {
    if (trucksWithShifts.length > 0) {
      const exists = trucksWithShifts.some((t) => t.truck?.id.toString() === selectedTruck);
      if (!exists) {
        // Let the preselected-truck effect apply it instead of overriding with a default truck which contains shift
        const preselectedExists =
          preselectedTruckId &&
          trucksWithShifts.some((t) => String(t.truck?.id) === preselectedTruckId);
        if (preselectedExists) {
          return;
        }

        // Opened with jobs already in the trip: a truck that cannot serve them
        // would drop the whole selection on arrival, so those come first.
        const servingCandidates = seedJobs.length
          ? trucksWithShifts.filter((item) =>
              seedJobs.every((job) => isJobServableByTruck(job, item.truck)),
            )
          : trucksWithShifts;
        const candidates = servingCandidates.length > 0 ? servingCandidates : trucksWithShifts;

        // Prefer a truck that has a shift by default, fall back to the first truck
        const defaultTruck =
          candidates.find((t) => (t.driverOnShifts?.length ?? 0) > 0) ?? candidates[0];
        setSelectedTruck(defaultTruck.truck?.id.toString() || "");
      }
    } else {
      setSelectedTruck("");
      setSelectedShiftId("");
    }
  }, [trucksWithShifts, selectedTruck, preselectedTruckId, seedJobs]);

  // Automatically select first shift of the selected truck when selectedTruck changes
  useEffect(() => {
    if (selectedTruck) {
      const selectedGroup = trucksWithShifts.find((t) => t.truck?.id.toString() === selectedTruck);
      if (
        selectedGroup &&
        selectedGroup.driverOnShifts &&
        selectedGroup.driverOnShifts.length > 0
      ) {
        const shiftIds = selectedGroup.driverOnShifts.map((s) => s.id.toString());
        if (!selectedShiftId || !shiftIds.includes(selectedShiftId)) {
          const activeShift = selectedGroup.driverOnShifts.find((s) => isShiftRunning(s.status));
          setSelectedShiftId(activeShift ? activeShift.id.toString() : shiftIds[0]);
        }
      } else {
        setSelectedShiftId("");
      }
    } else {
      setSelectedShiftId("");
    }
  }, [selectedTruck, trucksWithShifts, selectedShiftId]);

  // --- Trip capacity helpers -------------------------------------------------
  const activeTruckType = selectedTruckData?.truck?.type;

  // Max volume the truck can carry in one trip (m³), from its custom truck type.
  const truckVolume = selectedTruckData?.truck?.truckType?.volume ?? 0;

  // Trucks limited to a single job per trip (construction / multilift).
  const isSingleJobTruck = isSingleJobPerTripTruckType(activeTruckType);

  // Trucks limited by cargo volume per trip (MSW / liquid waste).
  const isVolumeLimitedTruck =
    activeTruckType === ConnectProto.TruckType.MSW ||
    activeTruckType === ConnectProto.TruckType.LIQUID_WASTE;

  // A pickup location can hold several container groups, but a job services
  // exactly one of them — only that group's containers count toward the cargo.
  const getJobVolume = useCallback(
    (jobId: number) => {
      const job = findJob(jobId);
      const group = job?.containerGroup;
      if (!job || !group) return 0;
      return (group.containerType?.volume ?? 0) * plannedContainerCount(job);
    },
    [findJob],
  );

  const getTripVolume = useCallback(
    (slots: (number | null)[]) =>
      slots.reduce<number>((sum, id) => (id !== null ? sum + getJobVolume(id) : sum), 0),
    [getJobVolume],
  );

  // Whether a trip made of `jobIds` may keep the newly added job. Only trucks
  // limited to a single job per trip (construction / multilift) auto-split;
  // volume-limited trucks keep the jobs and surface an "overloaded" warning.

  // Volume-limited truck whose trip cargo exceeds its capacity → show a warning.
  const isTripOverloaded = useCallback(
    (slots: (number | null)[]) =>
      isVolumeLimitedTruck && truckVolume > 0 && getTripVolume(slots) > truckVolume,
    [isVolumeLimitedTruck, truckVolume, getTripVolume],
  );
  const anyTripOverloaded = trips.some((tr) => isTripOverloaded(tr.jobSlots));

  // Job ids selected across all trips (used to avoid picking the same job twice).
  const allSelectedJobIds = useMemo(
    () => trips.flatMap((tr) => tr.jobSlots.filter((x): x is number => x !== null)),
    [trips],
  );

  // Issues present across all trips — any of them blocks submission, and each one
  // gets its own footer notice (a selection can hit both at once).
  const jobIssues = useMemo(() => {
    const issues = allSelectedJobIds.map(getJobIssue);
    return {
      typeMismatch: issues.includes("typeMismatch"),
      blacklisted: issues.includes("blacklisted"),
    };
  }, [allSelectedJobIds, getJobIssue]);

  const hasJobIssue = jobIssues.typeMismatch || jobIssues.blacklisted;

  // --- Re-distribute the selection when the truck changes --------------------
  //
  // Trips are built for one specific truck: its type decides which jobs it may
  // carry and how many fit in a single trip. Switching truck therefore keeps
  // the jobs the new truck can serve — re-split one per trip when it takes only
  // one at a time — and drops just the ones it cannot, instead of clearing the
  // whole selection. Runs on the first truck too, so jobs the modal was opened
  // with are checked against it as well.
  const appliedTruckRef = useRef<string | null>(null);
  // The same, as state: it commits together with the trips this effect sets, so
  // what waits on it (the group's auto-optimization) sees the filtered trips.
  const [appliedTruck, setAppliedTruck] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedTruck) {
      appliedTruckRef.current = null;
      setAppliedTruck(null);
      return;
    }
    if (appliedTruckRef.current === selectedTruck) return;

    const truck = selectedTruckData?.truck;
    // Judging compatibility before the truck's own record arrives would drop
    // every job — retry on the render that brings it.
    if (!truck) return;

    appliedTruckRef.current = selectedTruck;
    setAppliedTruck(selectedTruck);
    // A solve still running was for the previous truck's trips; a group's trips
    // are solved again for this truck's capacity.
    optimizeRequestRef.current++;
    autoOptimizedRef.current = false;
    if (allSelectedJobIds.length === 0) return;

    const kept = new Set(
      allSelectedJobIds.filter((jobId) => isJobServableByTruck(findJob(jobId), truck)),
    );

    const filtered = trips.map((tr) => ({
      ...tr,
      jobSlots: tr.jobSlots.filter((slot): slot is number => slot !== null && kept.has(slot)),
    }));
    const split = isSingleJobPerTripTruckType(truck.type)
      ? filtered.flatMap((tr) =>
          tr.jobSlots.length > 1
            ? tr.jobSlots.map((slot) => ({ id: makeTripId(), jobSlots: [slot], depot: tr.depot }))
            : [tr],
        )
      : filtered;
    const nextTrips = split.filter((tr) => tr.jobSlots.length > 0);

    const unchanged =
      nextTrips.length === trips.length &&
      nextTrips.every(
        (tr, index) =>
          tr.id === trips[index].id &&
          tr.jobSlots.length === trips[index].jobSlots.length &&
          tr.jobSlots.every((slot, slotIndex) => slot === trips[index].jobSlots[slotIndex]),
      );
    if (unchanged) return;

    // Nothing survived — start over, but keep the unloading point that was
    // already picked; it belongs to the depot list, not to the truck.
    const resolved =
      nextTrips.length > 0 ? nextTrips : [makeEmptyTrip(makeTripId(), trips[0]?.depot ?? null)];

    setTrips(resolved);
    setActiveTripId((current) =>
      resolved.some((tr) => tr.id === current) ? current : resolved[0].id,
    );
    setLastRouteCalculation("");
    setIsOptimized(false);
    // `trips`, `allSelectedJobIds` and `findJob` are read at the moment the truck
    // changes; the ref guard is what keeps this from re-running on their own churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTruck, selectedTruckData]);

  // A route takes only its owner's jobs: a shift of another division drops the rest.
  // Before a truck is picked the owner is unknown, so nothing is dropped; drafts stay, since the
  // backend sets their executor when it creates them.
  const [droppedForOwner, setDroppedForOwner] = useState(0);
  const appliedOwnerRef = useRef<string | null>(null);
  const routeOwnerKey = String(routeOwnerId ?? "");
  useEffect(() => {
    // A group's stops are the group's own: bulk create resolves their executor,
    // as it does for drafts.
    if (pickupLocationGroupId) return;
    if (!selectedTruckData?.truck) return;
    if (appliedOwnerRef.current === routeOwnerKey) return;
    appliedOwnerRef.current = routeOwnerKey;
    const foreign = new Set(
      allSelectedJobIds.filter((jobId) => {
        if (isDraftJobId(jobId)) return false;
        const provider = findJob(jobId)?.serviceProviderId;
        return !!provider && String(provider) !== routeOwnerKey;
      }),
    );
    setDroppedForOwner(foreign.size);
    if (foreign.size === 0) return;
    setTrips((prev) =>
      prev.map((tr) => ({
        ...tr,
        jobSlots: tr.jobSlots.filter((slot) => slot === null || !foreign.has(slot)),
      })),
    );
    setLastRouteCalculation("");
    setIsOptimized(false);
    // Read when the owner changes, like the truck effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeOwnerKey, selectedTruckData]);

  const resetTrips = (depot: number | null = null) => {
    tripIdCounter.current = 1;
    setPickedJobs({});
    setTrips([makeEmptyTrip("trip-1", depot)]);
    setActiveTripId("trip-1");
    setLastRouteCalculation("");
    setIsOptimized(false);
  };

  // A group's stops are per day: another date brings that day's stops in place
  // of the trips, and they are optimized again. The latest pick wins when the
  // answers arrive out of order.
  const [isLoadingStops, setIsLoadingStops] = useState(false);
  const stopsRequestRef = useRef(0);
  // The day the trips hold, which can lag selectedDate while a pick is loading.
  const loadedDateRef = useRef(selectedDate);
  const handleDateChange = async (date: Date) => {
    setSelectedDate(date);
    if (!loadGroupStops) return;
    const request = ++stopsRequestRef.current;
    optimizeRequestRef.current++;
    setIsLoadingStops(true);
    setSubmitError(null);
    setSuccessMessage(null);
    try {
      const { jobs, drafts: nextDrafts } = await loadGroupStops(date);
      if (request !== stopsRequestRef.current) return;
      loadedDateRef.current = date;
      const stopJobs = [...jobs, ...nextDrafts.map((draft) => draft.job)];
      setDrafts(nextDrafts);
      setPickedJobs(Object.fromEntries(stopJobs.map((job) => [job.id.toString(), job])));
      tripIdCounter.current = 1;
      setTrips([
        {
          id: "trip-1",
          jobSlots: stopJobs.length ? stopJobs.map((job) => Number(job.id)) : [null],
          depot: trips[0]?.depot ?? null,
        },
      ]);
      setActiveTripId("trip-1");
      setLastRouteCalculation("");
      setIsOptimized(false);
      autoOptimizedRef.current = false;
    } catch (error) {
      // The trips still hold the last loaded day's stops, so that day is picked again.
      if (request === stopsRequestRef.current) {
        setSelectedDate(loadedDateRef.current);
        setSubmitError(describeError(error as ApiError));
      }
    } finally {
      if (request === stopsRequestRef.current) setIsLoadingStops(false);
    }
  };

  const handleTruckChange = (truckId: string) => {
    setSelectedTruck(truckId);
    setJobLimitError(null); // Clear error when truck changes
    setSuccessMessage(null); // Clear success message on form change
    setIsErrorDismissed(false); // Reset error dismiss state
    setSubmitError(null);
    setOptimizeError(null);
    setOptimizeWarning(null);
    // The trips themselves are re-distributed by the effect above, which keeps
    // whatever the new truck can still serve.
  };

  // Append one or more jobs (picked in the modal) to the active trip. Fills any
  // trailing empty slot first, then appends. Count-limited trucks (construction /
  // multilift) push each extra job into its own new trip.
  const addJobsToActiveTrip = (jobIds: number[], pickedFromModal: ConnectProto.Job[] = []) => {
    setSuccessMessage(null);
    if (pickedFromModal.length > 0) {
      setPickedJobs((prev) => {
        const next = { ...prev };
        pickedFromModal.forEach((job) => {
          next[job.id.toString()] = job;
        });
        return next;
      });
    }
    const existing = new Set(allSelectedJobIds);
    const next = trips.map((tr) => ({ ...tr, jobSlots: [...tr.jobSlots] }));
    let activeId = activeTripId;
    const idxOf = (id: string) => next.findIndex((tr) => tr.id === id);

    jobIds.forEach((jobId) => {
      if (existing.has(jobId)) return;
      existing.add(jobId);

      const idx = idxOf(activeId);
      if (idx === -1) return;
      const trip = next[idx];
      const jobCount = trip.jobSlots.filter((x) => x !== null).length;

      if (isSingleJobTruck && jobCount >= 1) {
        const newTrip: Trip = { id: makeTripId(), jobSlots: [jobId], depot: trip.depot };
        next.splice(idx + 1, 0, newTrip);
        activeId = newTrip.id;
        return;
      }

      const emptyPos = trip.jobSlots.indexOf(null);
      if (emptyPos >= 0) {
        trip.jobSlots[emptyPos] = jobId;
      } else {
        trip.jobSlots.push(jobId);
      }
    });

    setTrips(next);
    setActiveTripId(activeId);
    setLastRouteCalculation("");
    setIsOptimized(false);
  };

  const openJobPickerModal = () => {
    openJobPicker({
      serviceProviderIds: activeCompanyId ? [BigInt(activeCompanyId)] : [],
      executedBy: executorIds,
      truckId: selectedTruck ? BigInt(selectedTruck) : undefined,
      // Only a picked shift names the owner; until then every executor's jobs are offered.
      executorId: selectedTruckData?.truck ? routeOwnerId : undefined,
      initialDate: selectedDate,
      excludeJobIds: allSelectedJobIds,
      onConfirm: addJobsToActiveTrip,
    });
  };

  const removeJobSlot = (slotIndex: number) => {
    const newJobSlots = jobSlots.filter((_, index) => index !== slotIndex);
    setJobSlots(newJobSlots.length === 0 ? [null] : newJobSlots);
    setLastRouteCalculation("");
    setIsOptimized(false);
  };

  // --- Trip management (three-dots menu) -------------------------------------
  const addTrip = () => {
    const newTrip = makeEmptyTrip(makeTripId(), activeTrip.depot);
    setTrips((prev) => [...prev, newTrip]);
    setActiveTripId(newTrip.id);
    setIsOptimized(false);
  };

  const removeActiveTrip = () => {
    if (trips.length <= 1) {
      resetTrips(activeTrip.depot);
      return;
    }
    const idx = trips.findIndex((tr) => tr.id === activeTripId);
    const remaining = trips.filter((tr) => tr.id !== activeTripId);
    setTrips(remaining);
    const nextActive = remaining[Math.max(0, idx - 1)] ?? remaining[0];
    setActiveTripId(nextActive.id);
    setLastRouteCalculation("");
    setSuccessMessage(null);
    setIsOptimized(false);
  };

  // Ask the VRP solver to split every selected job into capacity-feasible trips
  // for the current shift, then replace the local trips with its ordering.
  // The unloading point (disposal site) is the end of every solved trip, so it
  // has to be picked before optimization can run.
  const optimize = async () => {
    if (
      !(selectedShift?.id || pickupLocationGroupId) ||
      !selectedTruck ||
      !selectedDepot ||
      selectedDepot === NO_UNLOADING ||
      allSelectedJobIds.length === 0
    ) {
      return;
    }

    setOptimizeError(null);
    setOptimizeWarning(null);
    setSuccessMessage(null);
    const request = ++optimizeRequestRef.current;

    try {
      // Stops rather than job ids, so drafts that do not exist yet can be planned
      // too. Without a shift (group routes only) the truck alone is planned.
      const stopJobIds = allSelectedJobIds;
      const response = await solveSingleVehicle({
        stops: stopJobIds.map(toJobStop),
        disposalSiteId: BigInt(selectedDepot),
        driverOnShiftId: selectedShift?.id,
        truckId: BigInt(selectedTruck),
        date: toTimestamp(startOfDay(selectedDate)),
      }).unwrap();
      if (request !== optimizeRequestRef.current) return;

      if (response.responseStatus !== "ok") {
        setOptimizeError(response.errorMessage || t(Namespace.Routes, "optimizeFailed"));
        return;
      }

      const solvedTrips = response.trips
        .map((trip: ConnectProto.VrpTrip) => trip.stopIndexes.map((i) => stopJobIds[i]))
        .filter((jobIds: number[]) => jobIds.length > 0);

      // Jobs the solver could not fit are kept in a trailing trip so the
      // operator does not silently lose part of their selection.
      const unassignedJobIds = response.unassignedStopIndexes.map((i) => stopJobIds[i]);
      if (unassignedJobIds.length > 0) {
        solvedTrips.push(unassignedJobIds);
        setOptimizeWarning(
          t(Namespace.Routes, "optimizeUnassignedN", { count: unassignedJobIds.length }),
        );
      }

      if (solvedTrips.length === 0) {
        setOptimizeError(t(Namespace.Routes, "optimizeFailed"));
        return;
      }

      tripIdCounter.current = 0;
      const nextTrips = solvedTrips.map<Trip>((jobIds: number[]) => ({
        id: makeTripId(),
        jobSlots: jobIds,
        depot: selectedDepot,
      }));

      setTrips(nextTrips);
      setActiveTripId(nextTrips[0].id);
      setLastRouteCalculation("");
      setIsOptimized(true);
    } catch (error) {
      if (request !== optimizeRequestRef.current) return;
      console.error("Error optimizing trips:", error);
      setOptimizeError(t(Namespace.Routes, "optimizeFailed"));
    }
  };

  // Move a job out of its current trip into another existing trip, or into a
  // brand-new trip when targetTripId is null.
  const moveJobToTrip = (jobId: number, targetTripId: string | null) => {
    setSuccessMessage(null);
    const withoutJob = trips.map((tr) => {
      const filtered = tr.jobSlots.filter((slot) => slot !== jobId);
      return { ...tr, jobSlots: filtered.length ? filtered : [null] };
    });

    if (targetTripId === null) {
      const newTrip: Trip = {
        id: makeTripId(),
        jobSlots: [jobId],
        depot: activeTrip.depot,
      };
      setTrips([...withoutJob, newTrip]);
      setActiveTripId(newTrip.id);
    } else {
      setTrips(
        withoutJob.map((tr) =>
          tr.id === targetTripId
            ? { ...tr, jobSlots: [...tr.jobSlots.filter((s) => s !== null), jobId] }
            : tr,
        ),
      );
      setActiveTripId(targetTripId);
    }
    setLastRouteCalculation("");
    setIsOptimized(false);
  };

  const moveJobSlot = (fromIndex: number, toIndex: number) => {
    setJobSlots((prev) => {
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= prev.length ||
        toIndex >= prev.length ||
        fromIndex === toIndex
      ) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    // Order affects the calculated route, force a recalculation
    setLastRouteCalculation("");
    setSuccessMessage(null);
    setIsOptimized(false);
  };

  const selectedJobs = useMemo(
    () => jobSlots.filter((jobId): jobId is number => jobId !== null),
    [jobSlots],
  );

  const filteredDepots = useMemo(() => {
    if (!depotSearchQuery.trim()) {
      return depotsData;
    }

    const searchLower = normalizeForSearch(depotSearchQuery);
    return depotsData.filter((depot) => normalizeForSearch(depot.name).includes(searchLower));
  }, [depotsData, depotSearchQuery]);

  const getJobDisplayName = useCallback(
    (jobId: number | null) => {
      if (jobId === null) {
        return t(Namespace.Routes, "noAddress");
      }

      const job = findJob(jobId);
      return (
        job?.pickupLocation?.name ||
        job?.pickupLocation?.address ||
        t(Namespace.Routes, "noAddress")
      );
    },
    [findJob, t],
  );

  const getJobTypeLabel = useCallback(
    (jobId: number) => {
      const job = findJob(jobId);
      return job?.type !== undefined
        ? t(Namespace.Orders, `types.${ConnectProto.JobType[job.type]}` as any)
        : undefined;
    },
    [findJob, t],
  );

  const getJobContainersInfo = useCallback(
    (jobId: number) => {
      const job = findJob(jobId);
      const group = job?.containerGroup;
      // A job without a container group has nothing to show — an empty string
      // keeps the chip out of the row instead of rendering "undefined (undefined)".
      if (!group?.containerType?.name) return "";
      return `${group.containerType.name} (${group.numberOfContainers ?? 0})`;
    },
    [findJob],
  );

  // Customer (заказчик) the job was created for — shown on its own line in the row.
  const getJobCustomerName = useCallback(
    (jobId: number) => findJob(jobId)?.serviceReceiver?.name ?? "",
    [findJob],
  );

  const getJobArrivalTimeInfo = useCallback(
    (jobId: number) => {
      if (!routeData?.legs || !departInfo?.date_time) return null;

      const start = new Date(departInfo.date_time);
      if (isNaN(start.getTime())) return null;

      let currentMs = start.getTime();

      // Find the index of jobId in selectedJobs
      const index = selectedJobs.findIndex((id) => Number(id) === Number(jobId));
      if (index === -1) return null;

      // Iterate through legs up to index
      for (let i = 0; i <= index; i++) {
        const leg = routeData.legs[i];
        if (!leg) break;

        // Parse leg duration
        const legMinutes = (leg.steps ?? []).reduce((acc: number, step: any) => {
          return acc + parseISO8601Duration(step.duration || "PT0S");
        }, 0);

        currentMs += legMinutes * 60 * 1000;

        if (i === index) {
          break;
        }

        // Add service time of the previous job
        const prevJobId = selectedJobs[i];
        const prevJob = findJob(prevJobId);
        const prevPickup = prevJob?.pickupLocation;
        if (prevPickup) {
          const svc = prevPickup.serviceTime !== undefined ? prevPickup.serviceTime : 1800n;
          currentMs += Number(svc) * 1000;
        }
      }

      const arrivalDate = new Date(currentMs);

      // Format arrival time as HH:MM
      const arrivalTimeString = `${arrivalDate.getHours().toString().padStart(2, "0")}:${arrivalDate.getMinutes().toString().padStart(2, "0")}`;

      // Retrieve availability schedule of the target job
      const job = findJob(jobId);
      const pickup = job?.pickupLocation;
      const availabilitySchedule = pickup?.availabilitySchedule;

      if (!availabilitySchedule) {
        return { arrivalTimeString, comparison: null };
      }

      // Parse schedule e.g., "08:00 - 12:00" or "08:00-12:00"
      const match = availabilitySchedule.match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
      if (!match) {
        return { arrivalTimeString, comparison: null };
      }

      const startHours = parseInt(match[1], 10);
      const startMinutes = parseInt(match[2], 10);
      const endHours = parseInt(match[3], 10);
      const endMinutes = parseInt(match[4], 10);

      const startLimit = new Date(arrivalDate);
      startLimit.setHours(startHours, startMinutes, 0, 0);

      const endLimit = new Date(arrivalDate);
      endLimit.setHours(endHours, endMinutes, 0, 0);

      const arrivalTimeMs = arrivalDate.getTime();
      const startLimitMs = startLimit.getTime();
      const endLimitMs = endLimit.getTime();

      if (arrivalTimeMs < startLimitMs) {
        const diffMinutes = Math.round((startLimitMs - arrivalTimeMs) / 60000);
        return {
          arrivalTimeString,
          comparison: {
            status: "early",
            minutes: diffMinutes,
            text: t(Namespace.Routes, "earlyByMinutes").replace("{{minutes}}", String(diffMinutes)),
          },
        };
      } else if (arrivalTimeMs > endLimitMs) {
        const diffMinutes = Math.round((arrivalTimeMs - endLimitMs) / 60000);
        return {
          arrivalTimeString,
          comparison: {
            status: "late",
            minutes: diffMinutes,
            text: t(Namespace.Routes, "lateByMinutes").replace("{{minutes}}", String(diffMinutes)),
          },
        };
      } else {
        return {
          arrivalTimeString,
          comparison: {
            status: "on_time",
            minutes: 0,
            text: t(Namespace.Routes, "onTime"),
          },
        };
      }
    },
    [routeData, departInfo, selectedJobs, findJob, t],
  );

  const calculateRouteTime = useCallback(async () => {
    if (!selectedDepot || selectedJobs.length === 0) {
      return;
    }

    const cacheKey = `${selectedTruck}-${selectedJobs.join(",")}-${selectedDepot}`;

    if (lastRouteCalculation === cacheKey) {
      return;
    }

    try {
      // Undefined for NO_UNLOADING: the route ends at its last pickup.
      const selectedDepotData = depotsData.find((d) => d.id === selectedDepot);
      const source = resolveSource(selectedDepotData, selectedJobs[0]);
      if (!source) {
        return;
      }

      const waypoints = [];

      waypoints.push({
        lon: source.lon,
        lat: source.lat,
        date_time: new Date(),
      });

      selectedJobs.forEach((jobId) => {
        const job = findJob(jobId);
        if (job?.pickupLocation) {
          waypoints.push({
            lon: job.pickupLocation.lon,
            lat: job.pickupLocation.lat,
          });
        }
      });

      if (selectedDepotData) {
        waypoints.push({
          lon: selectedDepotData.lon,
          lat: selectedDepotData.lat,
        });
      }

      if (waypoints.length >= 2) {
        await calculateRouteWaypoints({ waypoints });
        setLastRouteCalculation(cacheKey);
      }
    } catch (error) {
      console.error("Error calculating route time:", error);
    }
  }, [
    selectedDepot,
    selectedJobs,
    selectedTruck,
    resolveSource,
    depotsData,
    findJob,
    lastRouteCalculation,
    calculateRouteWaypoints,
  ]);

  useEffect(() => {
    if (selectedJobs.length > 0 && selectedDepot && jobsById.size > 0) {
      calculateRouteTime();
    }
  }, [selectedJobs, selectedDepot, jobsById.size, calculateRouteTime]);

  // --- Map preview -----------------------------------------------------------
  // The map renders the active trip as if it were an already created route:
  // `previewRoute` is the shape MapWithGeofence hands to buildRouteVisualization,
  // which resolves the actual road geometry through calculateRouteWaypoints
  // (departure point → job pickups in list order → unloading depot).
  const selectedDepotData = useMemo(
    () => depotsData.find((d) => d.id === selectedDepot),
    [depotsData, selectedDepot],
  );

  // Departure point of the trip — the same rule calculateRouteTime and handleSubmit use.
  const routeSource = useMemo(
    () => resolveSource(selectedDepotData, selectedJobs[0]),
    [resolveSource, selectedDepotData, selectedJobs],
  );

  const activeTripJobs = useMemo(
    () =>
      selectedJobs
        .map((jobId) => findJob(jobId))
        .filter((job): job is ConnectProto.Job => Boolean(job)),
    [selectedJobs, findJob],
  );

  // Numbered by the job's place in the trip, as the list beside the map shows it.
  const mapObjects = useMemo<MapObject[]>(
    () => buildStepPins(activeTripJobs.map((job, index) => ({ job, step: String(index + 1) }))),
    [activeTripJobs],
  );

  const mapDepots = useMemo<MapObject[]>(() => {
    if (selectedDepotData?.lat == null || selectedDepotData?.lon == null) return [];
    return [
      {
        id: String(selectedDepotData.id),
        name: selectedDepotData.name,
        type: "depot" as InfoType,
        locationType: selectedDepotData.type,
        lat: selectedDepotData.lat,
        lon: selectedDepotData.lon,
      },
    ];
  }, [selectedDepotData]);

  // The truck marker doubles as the route's first waypoint, so it sits on the
  // departure point — the route does not exist yet, there is no GPS to follow.
  const mapTrucks = useMemo<MapObject[]>(() => {
    const truck = selectedTruckData?.truck;
    if (!truck || !routeSource) return [];
    return [
      {
        id: Number(truck.id),
        name: truck.plateNumber || String(truck.id),
        type: "truck" as InfoType,
        lat: routeSource.lat,
        lon: routeSource.lon,
        angle: 0,
        truckType:
          truck.type !== undefined
            ? `Truck_Type_${ConnectProto.TruckType[truck.type]}`
            : "TRUCK_TYPE_MSW",
        ownerOrganizationId: truck.ownerOrganizationId,
      },
    ];
  }, [selectedTruckData, routeSource]);

  const previewRoute = useMemo(() => {
    if (!routeSource || !selectedDepot || activeTripJobs.length === 0) return undefined;
    return {
      // CREATED makes the builder draw the whole trip as a planned (dashed) path.
      status: ConnectProto.RouteStatus.CREATED,
      jobs: activeTripJobs,
      driverOnShift: selectedShift,
      sourceLat: routeSource.lat,
      sourceLon: routeSource.lon,
      destinationLocationId: selectedDepotData ? BigInt(selectedDepotData.id) : undefined,
      destinationLat: selectedDepotData?.lat,
      destinationLon: selectedDepotData?.lon,
    } as unknown as ConnectProto.Route;
  }, [routeSource, selectedDepot, selectedDepotData, activeTripJobs, selectedShift]);

  const handleSubmit = async () => {
    if (!selectedTruck) {
      console.warn("Need to select a truck");
      return;
    }

    if (!selectedShift?.id && !pickupLocationGroupId) {
      console.warn("Missing truck shift information");
      return;
    }

    // One backend route is created per trip that has at least one job.
    const tripsToCreate = trips
      .map((trip) => ({
        trip,
        jobIds: trip.jobSlots.filter((x): x is number => x !== null),
      }))
      .filter(({ jobIds }) => jobIds.length > 0);

    if (tripsToCreate.length === 0) {
      console.warn("Need at least one job");
      return;
    }

    if (tripsToCreate.some(({ trip }) => trip.depot == null)) {
      console.warn("Each trip needs an unloading depot");
      return;
    }

    // Unsupported / blacklisted job — the footer already says why.
    if (hasJobIssue) {
      console.warn("Some jobs cannot be served by the selected truck");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    // Drafts created by this submit that no route has taken yet; cancelled if it fails.
    const strandedJobs = new Map<bigint, ConnectProto.Job>();
    try {
      // A Невывоз goes on the route as a re-issued attempt at its order, once the
      // dispatcher agrees; declining stops here.
      const routedJobs = [
        ...new Set(tripsToCreate.flatMap(({ jobIds }) => jobIds.filter((id) => !isDraftJobId(id)))),
      ].flatMap((jobId) => findJob(jobId) ?? []);
      const routeIdOf = await reissueExpiredJobs(routedJobs);
      if (!routeIdOf) return;

      if (pickupLocationGroupId) {
        const duplicateCount = tripsToCreate.reduce(
          (count, { jobIds }) => count + jobIds.filter((id) => duplicateOfById.has(id)).length,
          0,
        );
        // Picking the template mode is the dispatcher's go-ahead for duplicates.
        if (!groupTemplate && duplicateCount > 0 && !(await confirmDuplicates(duplicateCount)))
          return;

        // One transaction on the backend: it reuses or creates each stop's job
        // and, without a shift, opens one for the truck. Schedules only pick a
        // stop's window, never reject it. Unset source or destination falls
        // back to the truck's depot; without a shift there is no departure
        // point of ours to send, so the source is left to it.
        await createRoutesFromStops({
          allowDuplicates: groupTemplate || duplicateCount > 0,
          ownerOrganizationId: BigInt(activeCompanyId ?? 0),
          pickupLocationGroupId,
          date: toTimestamp(startOfDay(selectedDate)),
          driverOnShiftId: selectedShift?.id,
          truckId: BigInt(selectedTruck),
          trips: tripsToCreate.map(({ trip, jobIds }) => {
            const depotData = depotsData.find((d) => d.id === trip.depot);
            return {
              sourceLocationId: selectedShift ? resolveSource(depotData, jobIds[0])?.id : undefined,
              destinationLocationId: depotData ? BigInt(depotData.id) : undefined,
              // A re-issued Невывоз goes on as its new attempt.
              stops: jobIds.map((id) =>
                isDraftJobId(id) ? toJobStop(id) : { jobId: routeIdOf(id) },
              ),
            };
          }),
        }).unwrap();
        // The drafts now exist on the server; nothing is left to submit again.
        resetTrips();
        setSuccessMessage(t(Namespace.Infopanel, "modals.assignRoute.success"));
        onCreated?.();
        refetchJobs();
        return;
      }

      // Drafts have no server record yet. They are created first — in the order
      // the dispatcher arranged them, so their ids follow the route's stops —
      // and their real ids replace the synthetic ones below. If a job or a route
      // fails part way through, the jobs no route took are cancelled in the catch.
      const realIdByTempId = new Map<number, bigint>();
      const draftIds = [
        ...new Set(tripsToCreate.flatMap(({ jobIds }) => jobIds.filter(isDraftJobId))),
      ];

      if (draftIds.length > 0) {
        setIsCreatingJobs(true);
        const twStartAt = toTimestamp(startOfDay(selectedDate));
        const twEndAt = toTimestamp(endOfDay(selectedDate));
        try {
          for (const draftId of draftIds) {
            const draft = draftCreateById.get(draftId);
            // A slot holding a draft id with no payload cannot be created, and
            // dropping it silently would ship a route missing one of its stops.
            if (!draft) throw new Error(t(Namespace.Routes, "errorCreatingJobs"));

            const createdJob = await createJob({ ...draft, twStartAt, twEndAt }).unwrap();
            realIdByTempId.set(draftId, createdJob.id);
            strandedJobs.set(createdJob.id, createdJob);
          }
        } finally {
          setIsCreatingJobs(false);
        }
      }

      for (const { trip, jobIds } of tripsToCreate) {
        const depotData = depotsData.find((d) => d.id === trip.depot);
        if (!depotData && trip.depot !== NO_UNLOADING) continue;

        const source = resolveSource(depotData, jobIds[0]);
        if (!source) {
          throw new Error(t(Namespace.Routes, "errorCreatingRoute"));
        }
        const firstJob = findJob(jobIds[0]);
        const jobCategory = firstJob?.jobCategory;
        const routeCategory: ConnectProto.RouteCategory =
          (jobCategory != null ? JOB_CATEGORY_TO_GRPC_ROUTE_CATEGORY[jobCategory] : undefined) ??
          ConnectProto.RouteCategory.UNDEFINED;

        const routeJobIds = jobIds.map((jobId) => realIdByTempId.get(jobId) ?? routeIdOf(jobId));
        await createRouteWithJobs({
          create: {
            ownerOrganizationId: routeOwnerId ?? 0n,
            driverOnShiftId: selectedShift?.id,
            sourceLon: source.lon,
            sourceLat: source.lat,
            // Destination is all-or-nothing on the backend; none = no unloading.
            destinationLon: depotData?.lon,
            destinationLat: depotData?.lat,
            destinationLocationId: depotData ? BigInt(depotData.id) : undefined,
            routeCategory,
            sourceLocationId: source.id,
          },
          ownerOrganizationId: routeOwnerId ?? 0n,
          jobIds: routeJobIds,
        }).unwrap();
        routeJobIds.forEach((id) => strandedJobs.delete(id));
      }

      setSuccessMessage(t(Namespace.Infopanel, "modals.assignRoute.success"));
      onCreated?.();
      // Selected driver is not reset, it remains selected
      resetTrips();
      await new Promise((resolve) => setTimeout(resolve, 500));
      // Not unwrapped: the routes already exist, and a failed refresh is ignored
      // on purpose (the start point falls back to the depot), so don't unwrap it.
      await getDepartInfo(Number(selectedShift?.id), false);
      refetchJobs();
    } catch (error) {
      console.error("Error creating route:", error);
      // ponytail: cancel, not delete (the API has no delete), so they stay as «Отменено»;
      // all-or-nothing needs CreateRouteWithJobs to create the drafts itself.
      if (strandedJobs.size > 0) {
        const stranded = [...strandedJobs.values()];
        const results = await Promise.allSettled(
          stranded.map((job) =>
            cancelJob({
              id: job.id,
              serviceProviderId: job.serviceProviderId,
              serviceReceiverId: job.serviceReceiverId,
            }).unwrap(),
          ),
        );
        results.forEach((result, i) => {
          if (result.status === "rejected") {
            console.error(`Failed to cancel stranded job ${stranded[i].id}:`, result.reason);
          }
        });
        refetchJobs();
      }
      // The backend rejects what the client-side checks cannot see (e.g. stale
      // capabilities), so its reason has to reach the operator. getDepartInfo
      // above is REST and rejects without a code, which would otherwise render
      // as "unknown error" over the more useful fallback.
      const failure = error as Partial<ApiError>;
      const structured = failure?.code !== undefined || failure?.serverFault === true;
      setSubmitError(
        structured ? describeError(failure) : t(Namespace.Routes, "errorCreatingRoute"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // `createRouteError` is deliberately not part of these: it sticks around until
  // the next request, which would keep the submit button disabled forever after
  // one rejection. It is reported through the dismissible `submitError` instead.
  const hasError = !isErrorDismissed && (trucksError || jobsError || locationError || routeError);
  const hasAnyError = trucksError || jobsError || locationError || routeError || jobLimitError;
  // Every trip that has jobs must also have a depot; at least one job overall.
  const tripsWithJobs = trips.filter((tr) => tr.jobSlots.some((x) => x !== null));
  const allTripsHaveDepot = tripsWithJobs.every((tr) => tr.depot != null);
  const canSubmit =
    selectedTruck &&
    (selectedShift || pickupLocationGroupId) &&
    tripsWithJobs.length > 0 &&
    allTripsHaveDepot &&
    !hasAnyError &&
    !hasJobIssue &&
    !isLoadingStops;
  const isSubmittingRoute =
    isSubmitting ||
    isCreatingJobs ||
    isCreateRouteLoading ||
    isBulkCreateLoading ||
    isDepartInfoFetching;
  // Optimization needs a shift (truck capacity + depot), an unloading point and
  // at least one job to distribute.
  const canOptimize =
    Boolean(selectedShift?.id || (pickupLocationGroupId && selectedTruck)) &&
    selectedDepotData != null &&
    allSelectedJobIds.length > 0 &&
    !isLoadingStops;

  // A group's drafts open already optimized: split into trips by the truck's
  // capacity as soon as a truck and an unloading point are known. Once per
  // date and truck — later changes are the dispatcher's, and the button re-runs it.
  useEffect(() => {
    if (!pickupLocationGroupId || autoOptimizedRef.current || !canOptimize) return;
    // Wait for the truck-change effect to settle the trips for this truck.
    if (appliedTruck !== selectedTruck) return;
    autoOptimizedRef.current = true;
    optimize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupLocationGroupId, canOptimize, selectedTruck, appliedTruck, drafts]);

  // Picking an unloading point is free until the trips come from the optimizer;
  // after that it needs a confirmation because it invalidates the distribution.
  const handleSelectDepot = (depotId: number) => {
    setSuccessMessage(null);
    if (isOptimized && depotId !== selectedDepot) {
      setPendingDepotId(depotId);
      return;
    }
    setSelectedDepot(depotId);
  };

  // The optimizer solved every trip against a single disposal site, so the
  // confirmed depot is applied to all of them and the plan is no longer optimal.
  const confirmDepotChange = () => {
    if (pendingDepotId == null) return;
    setTrips((prev) => prev.map((tr) => ({ ...tr, depot: pendingDepotId })));
    setPendingDepotId(null);
    setIsOptimized(false);
    setOptimizeWarning(null);
    setLastRouteCalculation("");
  };

  const routeDurationText = useMemo(() => {
    // If no truck or jobs are selected, don't show the time
    if (!selectedTruck || selectedJobs.length === 0) {
      return "--";
    }

    if (isRouteCalculating) {
      return t(Namespace.Routes, "calculating");
    }

    if (routeData?.legs) {
      return calculateTotalRouteTimeWithService(
        routeData.legs,
        selectedJobs,
        jobsList,
        departInfo?.date_time,
        (key) => t(Namespace.Routes, key),
      );
    }

    return "--";
  }, [
    isRouteCalculating,
    routeData,
    selectedJobs,
    jobsList,
    departInfo?.date_time,
    selectedTruck,
    t,
  ]);

  const totalDistanceKm = useMemo(() => {
    if (!routeData?.legs) return null;
    const meters = routeData.legs.reduce(
      (sum: number, leg: any) =>
        sum + (leg.steps ?? []).reduce((s: number, step: any) => s + (step.length || 0), 0),
      0,
    );
    return meters > 0 ? Math.round(meters / 1000) : null;
  }, [routeData]);

  // Container types go down to 0.24 m³, so one decimal is too coarse — it would
  // round a 4 × 0.24 group up to a whole cubic metre.
  const formatVolume = useCallback(
    (volume: number) => `${Math.round(volume * 100) / 100} ${t(Namespace.Routes, "volumeUnit")}`,
    [t],
  );

  const getJobVolumeText = useCallback(
    (jobId: number) => {
      const volume = getJobVolume(jobId);
      return volume > 0 ? formatVolume(volume) : "";
    },
    [getJobVolume, formatVolume],
  );

  // Summary for the active trip's depot bar: jobs · volume · duration.
  const activeTripVolume = useMemo(() => getTripVolume(jobSlots), [getTripVolume, jobSlots]);
  const depotSummary = useMemo(() => {
    const parts = [`${selectedJobs.length} ${t(Namespace.Routes, "jobsCountShort")}`];
    if (activeTripVolume > 0) {
      parts.push(formatVolume(activeTripVolume));
    }
    if (routeDurationText && routeDurationText !== "--") {
      parts.push(routeDurationText);
    }
    return parts.join(" · ");
  }, [selectedJobs.length, activeTripVolume, routeDurationText, formatVolume, t]);

  // Footer "Итого" aggregates job count and volume across every trip.
  const totalVolume = useMemo(
    () => trips.reduce((sum, tr) => sum + getTripVolume(tr.jobSlots), 0),
    [trips, getTripVolume],
  );
  const tripsWithJobsCount = useMemo(
    () => trips.filter((tr) => tr.jobSlots.some((x) => x !== null)).length,
    [trips],
  );
  const totalSummary = useMemo(() => {
    const parts: string[] = [];
    if (tripsWithJobsCount > 1) {
      parts.push(`${tripsWithJobsCount} ${t(Namespace.Routes, "tripsCountShort")}`);
    }
    parts.push(`${allSelectedJobIds.length} ${t(Namespace.Routes, "jobsCountShort")}`);
    if (totalVolume > 0) {
      parts.push(formatVolume(totalVolume));
    }
    if (totalDistanceKm != null) {
      parts.push(`${totalDistanceKm} ${t(Namespace.Routes, "km")}`);
    }
    return parts.join(" · ");
  }, [tripsWithJobsCount, allSelectedJobIds.length, totalVolume, totalDistanceKm, formatVolume, t]);

  // Loading failures collapse into one footer notice; the per-source detail
  // moves into its tooltip.
  const loadErrorDetails = useMemo(
    () =>
      [
        trucksError && `Trucks: ${trucksError.toString()}`,
        jobsError && `Jobs: ${jobsError.toString()}`,
        locationError && `Locations: ${locationError.toString()}`,
        routeError && `Route calculation: ${routeError.toString()}`,
        createRouteError && `Route creation: ${createRouteError.toString()}`,
      ]
        .filter(Boolean)
        .join("\n"),
    [trucksError, jobsError, locationError, routeError, createRouteError],
  );

  const closeLabel = t(Namespace.Routes, "close");

  const footerMessages = (
    <>
      {jobIssues.typeMismatch && (
        <div className="flex items-center gap-1 text-sm font-medium text-text-error">
          <IcError className="h-6 w-6 shrink-0" />
          <span>{t(Namespace.Routes, "jobsNotAllowedForTruckType")}</span>
        </div>
      )}

      {droppedForOwner > 0 && (
        <div className="flex items-center gap-1 text-sm font-medium text-text-warning">
          <IcError className="h-6 w-6 shrink-0" />
          <span>{t(Namespace.Routes, "jobsDroppedForRouteOwner", { count: droppedForOwner })}</span>
        </div>
      )}

      {jobIssues.blacklisted && (
        <div className="flex items-center gap-1 text-sm font-medium text-text-warning">
          <IcError className="h-6 w-6 shrink-0" />
          <span>{t(Namespace.Routes, "truckConflictsWithLocationBlacklist")}</span>
        </div>
      )}

      {anyTripOverloaded && (
        <div className="flex items-center gap-1 text-sm font-medium text-brand-red">
          <IcError className="h-6 w-6 shrink-0" />
          <span>{t(Namespace.Routes, "overloadedByVolume")}</span>
        </div>
      )}

      {hasError && (
        <FooterMessage
          tone="error"
          text={t(Namespace.Routes, "errorLoadingData")}
          details={loadErrorDetails}
          closeLabel={closeLabel}
          onDismiss={() => setIsErrorDismissed(true)}
        />
      )}

      {jobLimitError && (
        <FooterMessage
          tone="error"
          text={jobLimitError}
          closeLabel={closeLabel}
          onDismiss={() => setJobLimitError(null)}
        />
      )}

      {submitError && (
        <FooterMessage
          tone="error"
          text={submitError}
          details={submitError}
          closeLabel={closeLabel}
          onDismiss={() => setSubmitError(null)}
        />
      )}

      {optimizeError && (
        <FooterMessage
          tone="error"
          text={optimizeError}
          closeLabel={closeLabel}
          onDismiss={() => setOptimizeError(null)}
        />
      )}

      {optimizeWarning && (
        <FooterMessage
          tone="warning"
          text={optimizeWarning}
          closeLabel={closeLabel}
          onDismiss={() => setOptimizeWarning(null)}
        />
      )}

      {successMessage && (
        <FooterMessage
          tone="success"
          text={successMessage}
          closeLabel={closeLabel}
          onDismiss={() => setSuccessMessage(null)}
        />
      )}
    </>
  );

  return (
    <div className="flex h-full flex-1 rounded-xl flex-col">
      <div className="flex min-h-0 flex-1 gap-4 p-4 border-t">
        <RouteSidebar
          selectedDate={selectedDate}
          onDateChange={(date) => date && handleDateChange(date)}
          selectedTruck={currentTruckStatusForSelected ?? undefined}
          isTruckPopoverOpen={isTruckPopoverOpen}
          onTruckPopoverChange={setIsTruckPopoverOpen}
          truckSearchQuery={truckSearchQuery}
          onTruckSearchQueryChange={setTruckSearchQuery}
          filteredTrucks={filteredTrucks}
          allTrucks={mappedCurrentTruckStatuses}
          onTruckSelect={handleTruckChange}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <ShiftHeaderCard
            shifts={selectedTruckData?.driverOnShifts || []}
            allShiftsOfDay={selectedTruckData?.allShiftsOfDay || []}
            selectedShiftId={selectedShiftId}
            onShiftSelect={setSelectedShiftId}
          />
          {/* min-h-0: without it this column sizes to its content, the job list
              never gets a bounded height and its overflow-y-auto can't scroll. */}
          <div className="flex min-h-0 flex-1 flex-col">
            <TripTabs
              trips={trips}
              activeTripId={activeTrip.id}
              onSelectTrip={setActiveTripId}
              onAddTrip={addTrip}
              onRemoveTrip={removeActiveTrip}
              onOptimize={optimize}
              canOptimize={canOptimize}
              isOptimizing={isOptimizing}
            />

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden border border-b-0 border-t-0  bg-white">
              <div className="flex flex-1 flex-col overflow-y-auto">
                <SortableJobList
                  jobSlots={jobSlots}
                  onReorder={moveJobSlot}
                  onRemoveJobSlot={removeJobSlot}
                  onOpenPicker={openJobPickerModal}
                  getJobDisplayName={getJobDisplayName}
                  getJobTypeLabel={getJobTypeLabel}
                  getJobContainersInfo={getJobContainersInfo}
                  getJobCustomerName={getJobCustomerName}
                  getJobVolumeText={getJobVolumeText}
                  getJobArrivalTimeInfo={getJobArrivalTimeInfo}
                  trips={trips}
                  activeTripId={activeTrip.id}
                  onMoveJobToTrip={moveJobToTrip}
                  getJobIssue={getJobIssue}
                  getJobDuplicateNote={getJobDuplicateNote}
                />
              </div>
            </div>
            <DepotBar
              depots={depotsData}
              filteredDepots={filteredDepots}
              depotsLoading={locationLoading}
              selectedDepot={selectedDepot}
              isDepotPopoverOpen={isDepotPopoverOpen}
              onDepotPopoverChange={setIsDepotPopoverOpen}
              depotSearchQuery={depotSearchQuery}
              onDepotSearchQueryChange={setDepotSearchQuery}
              onSelectDepot={handleSelectDepot}
              routeDurationText={depotSummary}
              noUnloadingId={NO_UNLOADING}
            />
          </div>
        </div>

        {/* Map Panel — unmounted while hidden so it stops rendering/tracking,
            and the job list takes over its width. */}
        {isMapVisible && (
          <div className="hidden min-h-0 w-[30%] shrink-0 flex-col lg:flex">
            <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-white">
              <MapWithGeofence
                previewRoute={previewRoute}
                objects={mapObjects}
                trucks={mapTrucks}
                depots={mapDepots}
                onMarkerClick={() => {}}
                className="h-full w-full z-0"
              />
            </div>
          </div>
        )}
      </div>

      <ModalFooter className="shrink-0">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex shrink-0 items-center gap-1 text-sm">
            <span className="text-text-secondary">{t(Namespace.Routes, "total")}:</span>
            <span className="font-medium text-text-primary">{totalSummary}</span>
          </div>
          <div className="flex min-w-0 flex-1 items-center justify-end gap-3">
            <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-1">
              {footerMessages}
            </div>
            <Button
              id="assign-route-submit-button"
              size="sm"
              variant="opacityBlueWithOutline"
              className="w-[200px] shrink-0"
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmittingRoute}
            >
              {isCreatingJobs
                ? t(Namespace.Routes, "creatingJobs")
                : isDepartInfoFetching
                  ? t(Namespace.Routes, "updatingSchedule")
                  : isSubmittingRoute
                    ? t(Namespace.Routes, "assigning")
                    : tripsWithJobsCount > 1
                      ? t(Namespace.Routes, "createRoutesN", { count: tripsWithJobsCount })
                      : t(Namespace.Routes, "assignRoute")}
            </Button>
          </div>
        </div>
      </ModalFooter>

      <DepotChangeConfirmModal
        open={pendingDepotId != null}
        onOpenChange={(open) => {
          if (!open) setPendingDepotId(null);
        }}
        depotName={
          pendingDepotId === NO_UNLOADING
            ? t(Namespace.Routes, "noUnloading")
            : depotsData.find((d) => d.id === pendingDepotId)?.name
        }
        onConfirm={confirmDepotChange}
      />
    </div>
  );
};
